// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { catchError, switchMap, tap } from 'rxjs/operators';
import { of, Observable } from 'rxjs';

// --- 3rdp Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { environment } from '../../../../environments/environment';
import { DemographicValueModel, ApplicationError, ToastModel, ErrorMessage } from '../../../models';
import { AppService } from '../app.service';
import { AppToastService } from '../app-toast.service';
import { DemographicsBaseService } from '../system/demographics-base.service';
import { UserDemographicsBaseService } from './user-demographics-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class UserDemographicsService extends UserDemographicsBaseService {
  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private toastService: AppToastService,
    appService: AppService,
    demographicService: DemographicsBaseService,
  ) {
    super(appService, demographicService);
  }

  loadFromUserDetails(userDemographicValues: DemographicValueModel[]) {
    this._userDemographicValues$.next(userDemographicValues);
    this._processUserDemographics(userDemographicValues);
  }

  updateUserDemographicsFromProfile(userDemographicValues: DemographicValueModel[]): void {
    if (!userDemographicValues || userDemographicValues.length === 0) {
      return;
    }

    const url =
      environment.apiConfig.apiUrl + environment.apiConfig.userDemographicsApi;

    this.http
      .put<DemographicValueModel[]>(url, userDemographicValues)
      .pipe(
        retryBackoff(environment.retryConfig),
        catchError(this._modelError.bind(this))
      ).subscribe(result => {
        if (!result) return;

        this.toastService.show({
          header: this.translate.instant('updateSuccessHeader'),
          body: this.translate.instant('updateUserDemographicsSuccessBody'),
          class: 'bg-success text-light'
        } as ToastModel);
        this._userDemographicValues$.next(result);
        this._processUserDemographics(result);
        this._updateUserDemographicsResult$.next(true);
      });
  }

  private _modelError(err: any): Observable<DemographicValueModel[] | null> {
    this._handleError(err);
    this._updateUserDemographicsResult$.next(false);
    return of(null);
  }

  private _handleError(err: any) {
    this.appService.setLoading(false);

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'UserDemographicsService',
      false,
      err
    );

    this.appService.setLastError(error);
  }
}
